<template>
  <div id="smooth-wrapper">
    <div id="smooth-content">
      <!-- Hint om saknad MD-fil -->
      <div v-if="!isLoading && displayedCategory && !displayedPage && isDev"
        class="mt-16 text-center mb-6  border border-amber-300 bg-amber-50 text-amber-800 px-4 py-3 text-sm">
        Add {{ displayedSlug }}.md to /content/products/ to add intro text for this category.
      </div>    
      <!-- Gemensam intro-komponent -->
      <ContentRenderer v-if="pageWithOnlyCategoryIntro" :value="pageWithOnlyCategoryIntro" :components="components" />

      <UPage>
        <UPageBody class="mt-0 lg:mt-0 pb-0">
          <div v-if="isLoading" class="p-6 text-center text-gray-500">
            Loading...
          </div>
          <div v-else-if="!displayedCategory" class="p-6 text-center text-gray-500">
            Category not found.
          </div>

          <!-- Modeller -->
          <div v-else class="space-y-16">
            <template v-for="pm in displayedModels" :key="pm.Style">
              <!-- Dynamiskt hero-product-block per modell -->

              <ContentRenderer v-if="pageWithHeroProduct(pm.Style)" :value="pageWithHeroProduct(pm.Style)"
                :components="components" />
              <WorkbookProductDisplay :model="pm" />
            </template>

            <!-- Nästa kategori-länk -->
            <div v-if="displayedNextCategory" class="mt-24 text-center" id="nav-footer">
              <UContainer>
                                
              </UContainer>
              <ContentCta :to="`/products/${displayedNextCategory.Slug}${categoryQuery}`">
                  <h2>Continue to {{ displayedNextCategory.Name }}</h2>                  
                </ContentCta>                
            </div>
            <div v-else class="mt-24 text-center">
              <ContentCta to="/products">
                  <h2>Go to line overview</h2>
                </ContentCta>
            </div>
          </div>
        </UPageBody>
      </UPage>
      
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted, provide } from 'vue'
import { onBeforeRouteUpdate, useRoute } from 'vue-router'
import { useCatalogStore } from '@/store/catalog'
import { useIntroStore } from '@/store/intro'
import type { ProductModel, CategoryModel } from '@/types/catalog'
import ContentCta from '~/components/content/Cta.vue'

import CategoryIntro from '~/components/content/CategoryIntro.vue'
import HeroProduct from '~/components/content/HeroProduct.vue'


const components = { CategoryIntro, HeroProduct }

const isDev = import.meta.dev
const route = useRoute()
const slug = computed(() => String(route.params.slug))
const catalogStore = useCatalogStore()
const introStore = useIntroStore()

const isLoading = computed(() => catalogStore.isLoading || !catalogStore.items.length)
const category = computed(() => catalogStore.categories.find(c => c.Slug === slug.value) || null)

// --- REAKTIV TOP CATEGORY (query-param) ---
const categoryParam = computed(() => String(route.query.category ?? route.query.mode ?? ''))
const topCategory = computed(() => {
  const raw = categoryParam.value
  if (!raw || raw === 'All') return ''
  const match = catalogStore.topCategories.find(cat => cat.toLowerCase() === raw.toLowerCase())
  return match ?? raw
})
const categoryQuery = computed(() => {
  if (!topCategory.value) return ''
  return `?category=${encodeURIComponent(topCategory.value.toLowerCase())}`
})
const topCategoryCategories = computed<CategoryModel[]>(() =>
  catalogStore.categoriesByTopCategory(topCategory.value || undefined)
)
function modelMatchesTopCategory(model: ProductModel): boolean {
  if (!topCategory.value) return true
  return model.variants.some(v => {
    const cat = v.Category
    return cat && String(cat) === topCategory.value
  })
}

// --- REAKTIVT: modeller filtreras på top category och kategori ---
const models = computed<ProductModel[]>(() => {
  const base = category.value?.ProductModels || []
  return base.filter(modelMatchesTopCategory)
})

// --- Transition freeze logic ---
const displayedSlug = ref(slug.value)
const displayedCategory = ref(category.value)
const displayedModels = ref(models.value)
const displayedPage = ref<any>(null)
const displayedNextCategory = ref<any>(null)
const isTransitioning = ref(false)
const displayedTopCategory = ref(topCategory.value)

provide('displayedRoute', {
  slug: displayedSlug,
  topCategory: displayedTopCategory,
})

const { data: page } = await useAsyncData(
  'product-page-' + slug.value,
  () => queryCollection('products').path('/products/' + slug.value).first()
)

/**
 * Filter blocks in a page by one or more blockTypes (comma-separated or array).
 * If blockType is "hero-product", blockProps.style can be used for further filtering.
 */
function filteredPageBlock(page: any, blockType: string | string[], blockProps: Record<string, any> = {}) {
  if (!page?.body?.value) return null

  let types: string[]
  if (Array.isArray(blockType)) {
    types = blockType.map(t => t.trim())
  } else {
    types = blockType.split(',').map(t => t.trim())
  }

  const blocks = page.body.value.filter(block => {
    if (!types.includes(block[0])) return false
    // Om style-filter är satt, filtrera på style för alla blockTypes som har style-prop
    if (blockProps.style && ['hero-product', 'chammy-tech'].includes(block[0])) {
      const styleVal = block[1][':style'] ?? block[1].style
      return String(styleVal) === String(blockProps.style)
    }
    return true
  })
  if (!blocks.length) return null
  return {
    ...page,
    body: {
      ...page.body,
      value: blocks
    }
  }
}

// Bara category-intro (visa bara om block finns)
const pageWithOnlyCategoryIntro = computed(() => {
  const block = filteredPageBlock(displayedPage.value, 'category-intro')
  return block && block.body.value.length ? block : null
})

function pageWithHeroProduct(style) {
  return filteredPageBlock(displayedPage.value, ['hero-product', 'chammy-tech'], { style })
}

// --- Nästa kategori-logik (ingen loop)
const nextCategory = computed<CategoryModel | null>(() => {
  // Välj rätt kategori-lista baserat på top category
  const cats: CategoryModel[] = topCategoryCategories.value
  
  if (!cats.length || !category.value) return null
  const idx = cats.findIndex(c => c.Slug === slug.value)
  if (idx === -1) return null
  if (idx === cats.length - 1) return null
  return cats[idx + 1]
})

const syncDisplayed = () => {
  if (isLoading.value) return
  displayedSlug.value = slug.value
  displayedTopCategory.value = topCategory.value
  displayedCategory.value = category.value
  displayedModels.value = models.value
  displayedPage.value = page.value
  displayedNextCategory.value = nextCategory.value
}

// Init + uppdatering vid route/data-ändring (fryses under transition)
watch(
  () => [isLoading.value, slug.value, topCategory.value, category.value, models.value, nextCategory.value],
  () => {
    if (isLoading.value) return
    if (isTransitioning.value) {
      return
    }
    syncDisplayed()
  },
  { immediate: true }
)

// Uppdatera page-data när den laddas, men bara om vi inte är i en transition
watch(
  page,
  (newPage) => {
    if (isLoading.value) return
    if (isTransitioning.value) {
      return
    }
    displayedPage.value = newPage
  }
)

onBeforeRouteUpdate(() => {
  if (process.client) {
    isTransitioning.value = true
  }
})

onMounted(() => {
  introStore.resetSlides()

  if (process.client) {
    const onStart = () => {
      isTransitioning.value = true
    }
    const onEnd = () => {
      isTransitioning.value = false
      syncDisplayed()
    }
    window.addEventListener('nuxt:page:transition:start', onStart)
    window.addEventListener('nuxt:page:transition:end', onEnd)

    onUnmounted(() => {
      window.removeEventListener('nuxt:page:transition:start', onStart)
      window.removeEventListener('nuxt:page:transition:end', onEnd)
    })
  }
})

onUnmounted(() => {
  if (window._introSmoother) {
    window._introSmoother.kill()
    window._introSmoother = null
  }
})

const fallbackTitle = computed(() =>
  displayedCategory.value ? `Kudo Sales demo – ${displayedCategory.value.Name}` : 'Kudo Sales demo – Products'
)
const fallbackDescription = computed(() =>
  displayedCategory.value?.Description || 'Kudo Sales demo.'
)

useContentSeo(page, {
  title: fallbackTitle,
  description: fallbackDescription,
})


</script>

<style scoped>
/* (valfria lokala hooks) */
</style>
